/**
 * JSON Patch (RFC 6902), four operations, for `giraffe_sdk_patch` (#41).
 *
 * The relay runs `validateOps` before a call is routed; the frame runs
 * `validateOps` and `applyPatch` on a record it has just read. Both import this
 * one file, so the rules cannot drift between them.
 *
 * ## Why hand-written
 *
 * Four ops and a pointer parser. `fast-json-patch` mutates in place by default,
 * has had a prototype-pollution CVE (CVE-2021-4279), and would ship in the
 * control-app bundle.
 *
 * ## `__proto__`, `constructor` and `prototype` are refused as path segments
 *
 * `obj["__proto__"] = v` sets the prototype rather than a key. A patch comes
 * from an agent, so a segment naming a prototype slot is refused before any
 * write.
 *
 * ## `move` and `copy` are refused
 *
 * No recipe needs them. `remove` then `add` expresses both.
 *
 * ## `add` is not safe to re-send
 *
 * `test` cannot assert that something is ABSENT, so nothing guards an `add` to
 * an array against a repeat: a client re-issuing it adds twice. A patch of
 * `test` + `replace` or `test` + `remove` is safe to re-send.
 */

/** Structurally `wire/json`'s `JsonValue`, restated because `manifest/` may not import `wire/`. */
export type Json = string | number | boolean | null | Json[] | { [k: string]: Json };

type JsonRecord = { [k: string]: Json };

export type PatchOp =
  | { readonly op: "test" | "replace" | "add"; readonly path: string; readonly value: Json }
  | { readonly op: "remove"; readonly path: string };

/** Ops that passed `validateOps`. Only `validateOps` makes one, so `applyPatch` never sees a raw op. */
export type ValidOps = readonly PatchOp[] & { readonly __brand: "ValidOps" };

export type PatchConflict = {
  /** Index into `ops` of the operation that stopped the patch. */
  readonly op: number;
  readonly path: string;
  /** `path_missing` whenever nothing is at `path`, so `actual: null` under `test_failed` means a real null. */
  readonly reason: "test_failed" | "path_missing";
  readonly expected: Json | null;
  readonly actual: Json | null;
};

type PatchOutcome =
  | {
      readonly ok: true;
      readonly record: Json;
      readonly changed: readonly string[];
      readonly undo: readonly PatchOp[];
    }
  | { readonly ok: false; readonly conflict: PatchConflict };

const OPS: ReadonlySet<string> = new Set(["test", "replace", "add", "remove"]);
const FORBIDDEN: ReadonlySet<string> = new Set(["__proto__", "constructor", "prototype"]);
const INDEX = /^(0|[1-9][0-9]*)$/;
const MISSING = Symbol("missing");

type Parsed =
  | { readonly ok: true; readonly tokens: readonly string[] }
  | { readonly ok: false; readonly message: string };

export function validateOps(
  raw: unknown,
):
  | { readonly ok: true; readonly ops: ValidOps }
  | { readonly ok: false; readonly message: string } {
  if (!Array.isArray(raw) || raw.length === 0) {
    return { ok: false, message: "ops: pass a non-empty array of JSON Patch operations" };
  }
  const ops: PatchOp[] = [];
  for (const [index, entry] of raw.entries()) {
    const at = `ops[${index}]`;
    if (typeof entry !== "object" || entry === null || Array.isArray(entry)) {
      return { ok: false, message: `${at}: an operation is an object with op and path` };
    }
    const fields = entry as Record<string, unknown>;
    const { op, path } = fields;
    if (op === "move" || op === "copy") {
      return { ok: false, message: `${at}.op: "${op}" is not supported; use remove and add` };
    }
    if (typeof op !== "string" || !OPS.has(op)) {
      return { ok: false, message: `${at}.op: must be one of test, replace, add, remove` };
    }
    if (typeof path !== "string") return { ok: false, message: `${at}.path: must be a string` };
    const parsed = parsePath(path);
    if (!parsed.ok) return { ok: false, message: `${at}.path: ${parsed.message}` };
    if (op === "remove") {
      ops.push({ op, path });
      continue;
    }
    if (!Object.hasOwn(fields, "value")) {
      return { ok: false, message: `${at}.value: "${op}" needs a value` };
    }
    ops.push({ op: op as "test" | "replace" | "add", path, value: fields.value as Json });
  }
  return { ok: true, ops: ops as unknown as ValidOps };
}

export function applyPatch(record: Json, ops: ValidOps): PatchOutcome {
  const root: Json = structuredClone(record);
  const changed: string[] = [];
  const inverse: PatchOp[] = [];

  for (const [index, op] of ops.entries()) {
    const parsed = parsePath(op.path);
    // Unreachable for ValidOps; reported rather than thrown so a frame never crashes on it.
    if (!parsed.ok) return conflict(index, op.path, "path_missing", null, MISSING);
    const tokens = parsed.tokens;
    const key = tokens[tokens.length - 1] as string;
    const parent = read(root, tokens.slice(0, -1));
    const current = read(root, tokens);
    const expected = "value" in op ? op.value : null;

    if (parent === MISSING || !(Array.isArray(parent) || isRecord(parent))) {
      return conflict(index, op.path, "path_missing", expected, MISSING);
    }

    if (op.op === "test") {
      if (current === MISSING) return conflict(index, op.path, "path_missing", op.value, MISSING);
      if (!jsonEqual(current, op.value)) {
        return conflict(index, op.path, "test_failed", op.value, current);
      }
      continue;
    }

    if (op.op === "remove") {
      if (current === MISSING) return conflict(index, op.path, "path_missing", null, MISSING);
      if (Array.isArray(parent)) parent.splice(Number(key), 1);
      else Reflect.deleteProperty(parent, key);
      inverse.push({ op: "add", path: op.path, value: current });
      changed.push(op.path);
      continue;
    }

    if (op.op === "replace") {
      if (current === MISSING) return conflict(index, op.path, "path_missing", op.value, MISSING);
      if (jsonEqual(current, op.value)) continue;
      if (Array.isArray(parent)) parent[Number(key)] = structuredClone(op.value);
      else parent[key] = structuredClone(op.value);
      inverse.push({ op: "replace", path: op.path, value: current });
      changed.push(op.path);
      continue;
    }

    // add
    if (Array.isArray(parent)) {
      const at = key === "-" ? parent.length : INDEX.test(key) ? Number(key) : -1;
      if (at < 0 || at > parent.length) {
        return conflict(index, op.path, "path_missing", op.value, MISSING);
      }
      parent.splice(at, 0, structuredClone(op.value));
      const concrete = toPointer([...tokens.slice(0, -1), String(at)]);
      inverse.push({ op: "remove", path: concrete });
      changed.push(concrete);
      continue;
    }
    if (current !== MISSING && jsonEqual(current, op.value)) continue;
    parent[key] = structuredClone(op.value);
    inverse.push(
      current === MISSING
        ? { op: "remove", path: op.path }
        : { op: "replace", path: op.path, value: current },
    );
    changed.push(op.path);
  }

  // Ops that cancel out change nothing, and a no-op must not be written.
  if (jsonEqual(root, record)) return { ok: true, record: root, changed: [], undo: [] };
  return { ok: true, record: root, changed: [...new Set(changed)], undo: inverse.reverse() };
}

export function valueAt(
  record: Json,
  path: string,
): { readonly present: true; readonly value: Json } | { readonly present: false } {
  const parsed = parsePath(path);
  if (!parsed.ok) return { present: false };
  const found = read(record, parsed.tokens);
  return found === MISSING ? { present: false } : { present: true, value: found };
}

export function jsonEqual(a: Json, b: Json): boolean {
  if (a === b) return true;
  if (Array.isArray(a)) {
    return (
      Array.isArray(b) && a.length === b.length && a.every((v, i) => jsonEqual(v, b[i] as Json))
    );
  }
  if (isRecord(a)) {
    if (!isRecord(b)) return false;
    const keys = Object.keys(a);
    return (
      keys.length === Object.keys(b).length &&
      keys.every((k) => Object.hasOwn(b, k) && jsonEqual(a[k] as Json, b[k] as Json))
    );
  }
  return false;
}

function parsePath(path: string): Parsed {
  if (path === "") {
    return {
      ok: false,
      message:
        'the whole record cannot be replaced; name the path that changes, e.g. "/public/counter/measures/3/name"',
    };
  }
  if (!path.startsWith("/")) {
    return { ok: false, message: `"${path}" is not a JSON Pointer; it must start with "/"` };
  }
  if (/~(?![01])/.test(path)) {
    return { ok: false, message: `"${path}" has a "~" that is not "~0" or "~1"` };
  }
  // RFC 6901: ~1 before ~0, so "~01" decodes to "~1" and not "/".
  const tokens = path
    .slice(1)
    .split("/")
    .map((token) => token.replace(/~1/g, "/").replace(/~0/g, "~"));
  const forbidden = tokens.find((token) => FORBIDDEN.has(token));
  if (forbidden !== undefined) {
    return {
      ok: false,
      message: `"${path}" names "${forbidden}", which is refused as a path segment`,
    };
  }
  return { ok: true, tokens };
}

function toPointer(tokens: readonly string[]): string {
  return tokens.map((token) => `/${token.replace(/~/g, "~0").replace(/\//g, "~1")}`).join("");
}

function isRecord(value: Json): value is JsonRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function read(root: Json, tokens: readonly string[]): Json | typeof MISSING {
  let at: Json = root;
  for (const token of tokens) {
    if (Array.isArray(at)) {
      if (!INDEX.test(token) || Number(token) >= at.length) return MISSING;
      at = at[Number(token)] as Json;
    } else if (isRecord(at)) {
      if (!Object.hasOwn(at, token)) return MISSING;
      at = at[token] as Json;
    } else {
      return MISSING;
    }
  }
  return at;
}

function conflict(
  op: number,
  path: string,
  reason: PatchConflict["reason"],
  expected: Json | null,
  actual: Json | typeof MISSING,
): PatchOutcome {
  return {
    ok: false,
    conflict: { op, path, reason, expected, actual: actual === MISSING ? null : actual },
  };
}
